// Projectiles - bullets, bombs, missiles
// All the things that go boom

import { Entity } from '../core/Entity';
import { VectorRenderer } from '../graphics/VectorRenderer';
import { VectorShape, Vec2 } from '../utils/types';

export type ProjectileType = 'bullet' | 'bomb' | 'missile' | 'enemyBullet';

const BULLET_SHAPE: VectorShape = {
  points: [
    { x: 0, y: -4 },
    { x: 0, y: 4 },
  ],
  closed: false,
};

const BOMB_SHAPE: VectorShape = {
  points: [
    { x: 0, y: -6 },
    { x: -4, y: 0 },
    { x: -3, y: 6 },
    { x: 3, y: 6 },
    { x: 4, y: 0 },
  ],
  closed: true,
};

const MISSILE_SHAPE: VectorShape = {
  points: [
    { x: 0, y: -8 },
    { x: -2, y: -4 },
    { x: -2, y: 4 },
    { x: -4, y: 8 },
    { x: 4, y: 8 },
    { x: 2, y: 4 },
    { x: 2, y: -4 },
  ],
  closed: true,
};

export class Projectile extends Entity {
  private projectileType: ProjectileType;
  private lifetime: number;
  private maxLifetime: number;
  private speed: number;
  private damage: number;
  private isPlayerOwned: boolean;
  private previousPosition: Vec2;

  constructor(
    x: number,
    y: number,
    angle: number,
    type: ProjectileType,
    initialVx: number = 0,
    initialVy: number = 0
  ) {
    super(x, y);
    this.projectileType = type;
    this.rotation = angle;
    this.lifetime = 0;

    switch (type) {
      case 'bullet':
        this.shape = BULLET_SHAPE;
        this.color = '#0f0';
        this.speed = 500;
        this.maxLifetime = 1.5;
        this.damage = 1;
        this.isPlayerOwned = true;
        break;
      case 'bomb':
        this.shape = BOMB_SHAPE;
        this.color = '#ff0';
        this.speed = 50; // bombs are slower, gravity will help
        this.maxLifetime = 3;
        this.damage = 3;
        this.isPlayerOwned = true;
        break;
      case 'missile':
        this.shape = MISSILE_SHAPE;
        this.color = '#f00';
        this.speed = 300;
        this.maxLifetime = 4;
        this.damage = 2;
        this.isPlayerOwned = false;
        break;
      case 'enemyBullet':
        this.shape = BULLET_SHAPE;
        this.color = '#f00';
        this.speed = 350;
        this.maxLifetime = 2;
        this.damage = 1;
        this.isPlayerOwned = false;
        break;
    }

    // Set initial velocity based on angle and add inherited velocity
    this.velocity.x = Math.sin(angle) * this.speed + initialVx;
    this.velocity.y = -Math.cos(angle) * this.speed + initialVy;

    // Initialize previous position for line collision detection
    this.previousPosition = { x: x, y: y };
  }

  update(dt: number): void {
    if (!this.active) return;

    // Update lifetime
    this.lifetime += dt;
    if (this.lifetime >= this.maxLifetime) {
      this.destroy();
      return;
    }

    // Store previous position for line collision detection
    this.previousPosition.x = this.position.x;
    this.previousPosition.y = this.position.y;

    // Bombs fall with gravity
    if (this.projectileType === 'bomb') {
      this.velocity.y += 150 * dt; // gravity
    }

    // Update position
    this.position.x += this.velocity.x * dt;
    this.position.y += this.velocity.y * dt;

    // Update rotation to match velocity direction for bombs
    if (this.projectileType === 'bomb') {
      this.rotation = Math.atan2(this.velocity.x, -this.velocity.y);
    }
  }

  render(renderer: VectorRenderer): void {
    if (!this.active) return;
    renderer.setGlow(4, this.color);
    super.render(renderer);
    renderer.clearGlow();
  }

  getType(): ProjectileType {
    return this.projectileType;
  }

  getDamage(): number {
    return this.damage;
  }

  isFromPlayer(): boolean {
    return this.isPlayerOwned;
  }

  getPreviousPosition(): Vec2 {
    return this.previousPosition;
  }
}
